import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';

/**
 * Format date for display with optional time
 * @param dateString - ISO date string or date object
 * @param withTime - Include time in the formatted output
 * @returns Formatted date string like "2nd Mar 2024" or "2nd Mar 2024, 10:30 AM"
 */
export const formatDate = (dateString: string | Date, withTime: boolean = false): string => {
  if (!dateString) {
    return '-';
  }

  try {
    const date = format(new Date(dateString), 'do MMM yyyy', { locale: enGB });
    if (withTime) {
      const time = new Date(dateString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${date}, ${time}`;
    }
    return date;
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format date for API (YYYY-MM-DD format)
 * @param date - Date object or string to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateForAPI = (date: Date | string): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

/**
 * Parse date from API format to Date object
 * @param dateString - Date string from API
 * @returns Date object or null if invalid
 */
export const parseDateFromAPI = (dateString: string): Date | null => {
  if (!dateString) return null;
  return new Date(dateString);
};

/**
 * Alternative simple date formatter (US locale)
 * @param dateString - ISO date string
 * @returns Formatted date string like "Jan 2, 2024" or "Not set"
 */
export const formatDateSimple = (dateString: string): string => {
  if (!dateString) return 'Not set';

  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};
